import React from 'react';
import {useLoaderData, useMatches} from '@remix-run/react';
import {Swiper, SwiperSlide} from 'swiper/react';

//components
import SanityImage from 'components/SanityImage';
import {Link} from 'components/Link';

//domains
import {sanityImgBuilder} from 'domains/sanity';

//styles
import 'swiper/swiper.min.css';

//types
import type {I18nLocale} from '~/lib/type';

import {loader} from '~/routes/($lang)/index';
import {brandHref} from '~/lib/brandCollections';

/**
 * Per-brand visual-compensation scale. The logo FILES carry wildly different
 * baked-in padding (measured ink coverage of each canvas: ROUGH AND ROAD's
 * mark fills only 15% of its height, FIVE 18%, GAERNE 26%, SHOEI 31% — while
 * KUSHITANI/FETURE/HELSTONS fill ~100%), so any uniform CSS box still renders
 * the VISIBLE marks at very different sizes. These factors were computed from
 * the measured ink bounding-box of every asset so each visible mark lands at
 * ~44px tall (or the tile's width limit for thin wordmarks). transform:scale
 * doesn't affect layout, and the overflowing padding is transparent/white
 * (invisible under mix-blend-multiply). Re-measure if a logo asset changes.
 */
const LOGO_SCALE: Record<string, number> = {
  CARDO: 1.43,
  DOPPELGANGER: 1.15,
  FETURE: 1.35,
  'FIVE GLOVES': 3.27,
  FURYGAN: 1.6,
  GAERNE: 3.07,
  HELSTONS: 0.79,
  INSTA360: 2.4,
  'JOHN DOE': 2.17,
  KUSHITANI: 0.79,
  'OVER RACING': 2.16,
  'ROUGH AND ROAD': 3.39,
  // Measured 1.27, then +50% by eye — the mark's thin scorpion linework
  // reads smaller than solid logos at equal bounding-box height.
  SCORPION: 1.9,
  SHOEI: 2.55,
};

function Chevron({dir}: {dir: 'left' | 'right'}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <path d={dir === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
    </svg>
  );
}

/**
 * Homepage "推薦品牌 / Featured Brands" carousel, rendered directly above the
 * 店長推薦 marquee. A RevZilla-style logo strip: every brand that has a logo in
 * Sanity, shown in a horizontal carousel with prev/next arrows and dividers.
 * Logos are backed by bg-white + mix-blend-multiply so the white background
 * baked into the JPG/PNG assets drops away. Each tile links to the brand PLP
 * (/collections/vendor?pf_v_brand=<vendor>) — same target as the mega-menu.
 */
const RecommendedBrands: React.FC = () => {
  const {recommendedBrands} = useLoaderData<typeof loader>();
  const [root] = useMatches();
  const selectedLocale = root?.data?.selectedLocale as I18nLocale | undefined;
  const isZh = selectedLocale?.language !== 'EN';
  // Drive the arrows imperatively via the Swiper instance — wiring DOM refs
  // into the Navigation module raced React's render cycle (the refs were
  // still null when Swiper initialised), leaving the arrows dead.
  const swiperRef = React.useRef<{
    slidePrev: () => void;
    slideNext: () => void;
  } | null>(null);

  if (!recommendedBrands || recommendedBrands.length === 0) return <></>;

  return (
    // bg-white backs the logos so mix-blend-multiply (below) can drop the white
    // background baked into the JPG/PNG logos without touching the assets.
    <section className="bg-white pt-14 pb-2">
      <header className="mb-8 text-center">
        <h2 className="text-xl md:text-section font-bold uppercase tracking-wide">
          {isZh ? '推薦品牌' : 'Featured Brands'}
        </h2>
      </header>
      <div className="mx-auto flex max-w-6xl items-center gap-1 px-2 md:gap-3 md:px-4">
        <button
          type="button"
          aria-label={isZh ? '上一個品牌' : 'Previous brands'}
          onClick={() => swiperRef.current?.slidePrev()}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-hk-orange"
        >
          <Chevron dir="left" />
        </button>
        <Swiper
          className="brands-carousel min-w-0 flex-1"
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          // Loop so BOTH arrows always respond — without it slidePrev() is a
          // no-op while the carousel sits at the first page.
          loop
          spaceBetween={0}
          slidesPerView={2}
          slidesPerGroup={2}
          breakpoints={{
            // Fewer slides per view than before = wider tiles = bigger logos,
            // matching RevZilla's trending-brands proportions.
            640: {slidesPerView: 3, slidesPerGroup: 3},
            768: {slidesPerView: 4, slidesPerGroup: 4},
            1024: {slidesPerView: 5, slidesPerGroup: 5},
          }}
        >
          {recommendedBrands.map((brand) => (
            <SwiperSlide key={brand.shopify_brand_node}>
              <Link
                to={brandHref(brand.shopify_brand_node)}
                prefetch="intent"
                title={brand.title}
                className="flex h-20 w-full items-center justify-center px-2 transition-opacity duration-200 hover:opacity-70 md:h-24 md:px-4"
              >
                {brand.brand_logo?.asset?._ref ? (
                  // Wrapper carries the per-brand scale (SanityImage strips
                  // its style prop): RevZilla-proportion base box on the img
                  // (max-h 40/56px, max-w 88%) + scale compensating each
                  // file's baked-in padding (see LOGO_SCALE) so the VISIBLE
                  // marks all land at the same size.
                  <span
                    className="flex max-w-full items-center justify-center"
                    style={{
                      transform: `scale(${
                        LOGO_SCALE[brand.shopify_brand_node] ?? 1
                      })`,
                    }}
                  >
                    {brand.brand_logo.asset._ref.endsWith('-svg') ? (
                      // SVG uploads (e.g. SCORPION) carry a viewBox but no
                      // width/height attributes, and the Sanity CDN ignores
                      // resize params on raw SVGs — so the <img> gets no
                      // intrinsic size and collapses to nothing. Rasterise
                      // through the CDN (fm=png) to give the browser real
                      // pixel dimensions.
                      <img
                        src={sanityImgBuilder
                          .image({_ref: brand.brand_logo.asset._ref})
                          .width(640)
                          .format('png')
                          .quality(80)
                          .url()}
                        alt={brand.title}
                        loading="lazy"
                        decoding="async"
                        className="max-h-10 w-auto max-w-full object-contain mix-blend-multiply md:max-h-14"
                      />
                    ) : (
                      <SanityImage
                        src={brand.brand_logo.asset._ref}
                        sizes={['150px']}
                        alt={brand.title}
                        loading="lazy"
                        className="max-h-10 w-auto max-w-full object-contain mix-blend-multiply md:max-h-14"
                      />
                    )}
                  </span>
                ) : (
                  <span className="text-center text-base font-bold uppercase tracking-wide text-hk-deep-gray">
                    {brand.title}
                  </span>
                )}
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>
        <button
          type="button"
          aria-label={isZh ? '下一個品牌' : 'Next brands'}
          onClick={() => swiperRef.current?.slideNext()}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-hk-orange"
        >
          <Chevron dir="right" />
        </button>
      </div>
    </section>
  );
};

export default RecommendedBrands;
